import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Chip,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupsIcon from '@mui/icons-material/Groups';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HandshakeIcon from '@mui/icons-material/Handshake';
import BuildIcon from '@mui/icons-material/Build';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import RouteIcon from '@mui/icons-material/Route';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Hero Slider Slides
  const heroSlides = [
    {
      title: "ADVAIT ROAD MOVERS",
      subtitle: "India's Leading Logistics & Transportation Provider",
      description: "Delivering excellence with 20000+ tons monthly capacity and seamless connectivity across India",
      gradient: "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)",
      icon: <LocalShippingIcon sx={{ fontSize: { xs: 100, md: 150 }, opacity: 0.2 }} />,
      bgPattern: "truck"
    },
    {
      title: "Fast & Reliable Delivery",
      subtitle: "Time-Bound Transportation Solutions",
      description: "Economically efficient & time-bound transportation with seamless connectivity through our dedicated fleet",
      gradient: "linear-gradient(135deg, #0d47a1 0%, #1976d2 50%, #42a5f5 100%)",
      icon: <SpeedIcon sx={{ fontSize: { xs: 100, md: 150 }, opacity: 0.2 }} />,
      bgPattern: "speed"
    },
    {
      title: "Nationwide Network",
      subtitle: "Connected Across India",
      description: "End-to-end solutions for transport & logistics needs under one roof, improving business efficiencies",
      gradient: "linear-gradient(135deg, #1565c0 0%, #2979ff 50%, #64b5f6 100%)",
      icon: <RouteIcon sx={{ fontSize: { xs: 100, md: 150 }, opacity: 0.2 }} />,
      bgPattern: "network"
    },
    {
      title: "ISO Certified Excellence",
      subtitle: "Safety & Compliance First",
      description: "ISO 9001:2008 Certified company with safety and compliance policies that inspire excellence",
      gradient: "linear-gradient(135deg, #283593 0%, #3949ab 50%, #5c6bc0 100%)",
      icon: <VerifiedIcon sx={{ fontSize: { xs: 100, md: 150 }, opacity: 0.2 }} />,
      bgPattern: "certified"
    }
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Auto-play slider
  useEffect(() => {
    autoPlayRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000); // Change slide every 5 seconds

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [heroSlides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    autoPlayRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
  };

  const nextSlide = () => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
  };

  const stats = [
    { 
      value: '20000+', 
      label: 'Tons Monthly', 
      icon: <LocalShippingIcon />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea',
      bgColor: '#f3f0ff'
    },
    { 
      value: '24/7', 
      label: 'Support', 
      icon: <SupportAgentIcon />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#f5576c',
      bgColor: '#fff0f3'
    },
    { 
      value: 'ISO 9001:2008', 
      label: 'Certified', 
      icon: <VerifiedIcon />,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#4facfe',
      bgColor: '#e6f7ff'
    },
    { 
      value: '100%', 
      label: 'Reliability', 
      icon: <TrendingUpIcon />,
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      color: '#43e97b',
      bgColor: '#e6fff3'
    },
  ];

  const features = [
    {
      icon: <SpeedIcon sx={{ fontSize: 48 }} />,
      title: "FASTEST GOODS DELIVERY",
      description: "Offers economically efficient & time bound transportation with seamless connectivity through a dedicated fleet of vehicle & trusted partners.",
      color: '#1976d2'
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 48 }} />,
      title: "24/7 ON-CALL SUPPORT",
      description: "Our 24x7 on-call support provides quality services to our concerned customers at anytime. Give us a call using one of the phone numbers.",
      color: '#2e7d32'
    },
    {
      icon: <IntegrationInstructionsIcon sx={{ fontSize: 48 }} />,
      title: "END-TO-END SOLUTIONS",
      description: "Provide End-to-End solutions for clients transport & logistics needs under one roof, improving their business efficiencies and productivity.",
      color: '#ed6c02'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48 }} />,
      title: "SAFETY & COMPLIANCE",
      description: "We are an ISO 9001:2008 Certified company. Safety and compliance policies & procedures that inspire corporate and individual excellence.",
      color: '#d32f2f'
    }
  ];

  const values = [
    { label: 'Commitments', icon: <EmojiEventsIcon sx={{ fontSize: 36 }} /> },
    { label: 'Ownership', icon: <VerifiedUserIcon sx={{ fontSize: 36 }} /> },
    { label: 'Teamwork', icon: <GroupsIcon sx={{ fontSize: 36 }} /> },
    { label: 'Innovation', icon: <LightbulbIcon sx={{ fontSize: 36 }} /> },
    { label: 'Integrity', icon: <HandshakeIcon sx={{ fontSize: 36 }} /> },
    { label: 'Relationship Building', icon: <BuildIcon sx={{ fontSize: 36 }} /> },
    { label: 'Flexibility', icon: <CompareArrowsIcon sx={{ fontSize: 36 }} /> },
  ];

  return (
    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
      {/* Hero Slider Section */}
      <Box
        ref={heroRef}
        sx={{
          position: 'relative',
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          overflow: 'hidden',
        }}
      >
        {/* Slider Container */}
        <Box
          ref={sliderRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: '90vh',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden'
          }}
        >
          {/* Slides */}
          {heroSlides.map((slide, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                minHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                background: slide.gradient,
                opacity: index === currentSlide ? 1 : 0,
                transition: 'opacity 1.5s ease-in-out',
                zIndex: index === currentSlide ? 2 : 1,
                pointerEvents: index === currentSlide ? 'auto' : 'none',
                visibility: index === currentSlide ? 'visible' : 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                  pointerEvents: 'none'
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: 0.1,
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '40px 40px',
                  animation: 'move 20s linear infinite',
                  '@keyframes move': {
                    '0%': { transform: 'translate(0, 0)' },
                    '100%': { transform: 'translate(40px, 40px)' }
                  }
                }
              }}
            >
              <Container sx={{ position: 'relative', zIndex: 3 }}>
                <Grid container spacing={4} alignItems="center" sx={{ py: { xs: 4, md: 8 } }}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        opacity: index === currentSlide ? 1 : 0,
                        transform: index === currentSlide ? 'translateY(0)' : 'translateY(20px)',
                        transition: 'opacity 1.5s ease-in-out 0.3s, transform 1.5s ease-in-out 0.3s'
                      }}
                    >
                        <Chip 
                          label={slide.subtitle}
                          sx={{ 
                            mb: 3, 
                            bgcolor: 'rgba(255,255,255,0.2)', 
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.3)'
                          }} 
                        />
                        <Typography 
                          variant={isMobile ? "h3" : "h2"} 
                          component="h1" 
                          gutterBottom
                          sx={{ 
                            fontWeight: 800,
                            lineHeight: 1.2,
                            mb: 3,
                            textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
                            animation: index === currentSlide ? 'fadeInUp 0.8s ease-out' : 'none',
                            '@keyframes fadeInUp': {
                              '0%': { opacity: 0, transform: 'translateY(30px)' },
                              '100%': { opacity: 1, transform: 'translateY(0)' }
                            }
                          }}
                        >
                          {slide.title}
                        </Typography>
                        <Typography 
                          variant={isMobile ? "body1" : "h6"} 
                          paragraph
                          sx={{ 
                            mb: 4,
                            opacity: 0.95,
                            lineHeight: 1.7,
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            textShadow: '1px 1px 4px rgba(0,0,0,0.3)',
                            animation: index === currentSlide ? 'fadeInUp 1s ease-out 0.2s both' : 'none',
                          }}
                        >
                          {slide.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate('/login')}
                            sx={{
                              bgcolor: '#fff',
                              color: '#1a237e',
                              px: 4,
                              py: 1.5,
                              fontSize: '1rem',
                              fontWeight: 600,
                              borderRadius: '50px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                              '&:hover': {
                                bgcolor: '#f5f5f5',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
                              },
                              transition: 'all 0.3s ease',
                              animation: index === currentSlide ? 'fadeInUp 1s ease-out 0.4s both' : 'none',
                            }}
                          >
                            Login to Dashboard
                          </Button>
                          <Button
                            variant="outlined"
                            size="large"
                            sx={{
                              borderColor: 'white',
                              color: 'white',
                              px: 4,
                              py: 1.5,
                              fontSize: '1rem',
                              fontWeight: 600,
                              borderRadius: '50px',
                              borderWidth: 2,
                              '&:hover': {
                                borderColor: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)',
                                transform: 'translateY(-2px)',
                                borderWidth: 2,
                              },
                              transition: 'all 0.3s ease',
                              animation: index === currentSlide ? 'fadeInUp 1s ease-out 0.5s both' : 'none',
                            }}
                            onClick={() => document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' })}
                          >
                            Learn More
                          </Button>
                        </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        opacity: index === currentSlide ? 1 : 0,
                        transform: index === currentSlide ? 'scale(1)' : 'scale(0.95)',
                        transition: 'opacity 1.5s ease-in-out 0.5s, transform 1.5s ease-in-out 0.5s'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          position: 'relative'
                        }}
                      >
                        {index === 0 ? (
                          <Box
                            component="img"
                            src="/logo.jpg"
                            alt="Advait Road Movers Logo"
                            sx={{
                              width: '100%',
                              maxWidth: 500,
                              height: 'auto',
                              display: 'block',
                              margin: 'auto',
                              borderRadius: '20px',
                              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                              transition: 'transform 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.05)',
                              }
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              maxWidth: 500,
                              aspectRatio: '1/1',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '& svg': {
                                filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
                                animation: 'float 3s ease-in-out infinite',
                                '@keyframes float': {
                                  '0%, 100%': { transform: 'translateY(0px)' },
                                  '50%': { transform: 'translateY(-20px)' }
                                }
                              }
                            }}
                          >
                            {slide.icon}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          ))}

          {/* Navigation Arrows */}
          <IconButton
            onClick={prevSlide}
            sx={{
              position: 'absolute',
              left: { xs: 10, md: 30 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
                transform: 'translateY(-50%) scale(1.1)',
              },
              transition: 'all 0.3s ease',
              width: { xs: 40, md: 50 },
              height: { xs: 40, md: 50 }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <IconButton
            onClick={nextSlide}
            sx={{
              position: 'absolute',
              right: { xs: 10, md: 30 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
                transform: 'translateY(-50%) scale(1.1)',
              },
              transition: 'all 0.3s ease',
              width: { xs: 40, md: 50 },
              height: { xs: 40, md: 50 }
            }}
          >
            <ArrowForwardIcon />
          </IconButton>

          {/* Slider Dots */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 20, md: 40 },
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              display: 'flex',
              gap: 1
            }}
          >
            {heroSlides.map((_, index) => (
              <Box
                key={index}
                onClick={() => goToSlide(index)}
                sx={{
                  width: currentSlide === index ? 40 : 12,
                  height: 12,
                  borderRadius: 6,
                  bgcolor: currentSlide === index ? 'white' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.8)',
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Statistics Section */}
      <Box
        ref={statsRef}
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: 'white',
          position: 'relative',
          mt: { xs: -4, md: -6 },
          mb: 4,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(25,118,210,0.2), transparent)'
          }
        }}
      >
        <Container>
          <Typography
            variant="h4"
            align="center"
            sx={{
              fontWeight: 700,
              color: '#1a237e',
              mb: 5,
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            Our Achievements
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Fade in={isVisible} timeout={1000} style={{ transitionDelay: `${index * 150}ms` }}>
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: { xs: 2.5, md: 4 },
                      height: '100%',
                      background: stat.bgColor,
                      borderRadius: '24px',
                      border: `2px solid transparent`,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: stat.gradient,
                        opacity: 0,
                        transition: 'opacity 0.4s ease',
                        zIndex: 0
                      },
                      '&:hover': {
                        transform: 'translateY(-12px) scale(1.02)',
                        boxShadow: `0 20px 60px ${stat.color}40`,
                        border: `2px solid ${stat.color}`,
                        '&::before': {
                          opacity: 0.1
                        },
                        '& .stat-icon': {
                          transform: 'scale(1.2) rotate(5deg)',
                          color: stat.color
                        },
                        '& .stat-value': {
                          color: stat.color,
                          transform: 'scale(1.1)'
                        }
                      }
                    }}
                  >
                    <Box
                      className="stat-icon"
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: { xs: 60, md: 80 },
                        height: { xs: 60, md: 80 },
                        mx: 'auto',
                        borderRadius: '50%',
                        background: stat.gradient,
                        color: 'white',
                        transition: 'all 0.4s ease',
                        boxShadow: `0 8px 24px ${stat.color}40`,
                        '& svg': {
                          fontSize: { xs: 32, md: 40 }
                        }
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Typography
                      className="stat-value"
                      variant="h3"
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        fontWeight: 900,
                        background: stat.gradient,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1.5,
                        fontSize: { xs: '1.75rem', md: '2.5rem' },
                        transition: 'all 0.4s ease',
                        lineHeight: 1.2
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        color: '#555',
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container sx={{ py: { xs: 6, md: 10 } }} id="features-section">
        <Typography 
          variant="h3" 
          component="h2" 
          align="center" 
          gutterBottom
          sx={{ 
            fontWeight: 800,
            mb: 2,
            color: '#1a237e'
          }}
        >
          Why Choose Us
        </Typography>
        <Typography 
          variant="body1" 
          align="center" 
          sx={{ mb: 6, color: '#666', maxWidth: 600, mx: 'auto' }}
        >
          Delivering excellence in logistics and transportation services across India
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Fade in={isVisible} timeout={1000} style={{ transitionDelay: `${index * 150}ms` }}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: '20px',
                    background: 'white',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: `linear-gradient(90deg, ${feature.color}, ${feature.color}dd)`,
                      transform: 'scaleX(0)',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: `0 20px 40px rgba(25, 118, 210, 0.2)`,
                      '&::before': {
                        transform: 'scaleX(1)'
                      },
                      '& .feature-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                        color: feature.color
                      }
                    }
                  }}
                >
                  <CardContent>
                    <Box
                      className="feature-icon"
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        color: feature.color,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      sx={{ 
                        mb: 2,
                        fontWeight: 700,
                        color: '#1a237e',
                        textAlign: 'center'
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        textAlign: 'center',
                        lineHeight: 1.7
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Our Valuable Customers Section */}
      <Box 
        sx={{ 
          bgcolor: '#f8f9fa',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(25,118,210,0.2), transparent)'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(25,118,210,0.2), transparent)'
          }
        }}
      >
        <Container>
          <Typography
            variant="h3"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 800,
              mb: 2,
              color: '#1a237e',
              fontSize: { xs: '1.75rem', md: '2.5rem' }
            }}
          >
            Our Valuable Customers
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 6,
              color: '#666',
              maxWidth: 700,
              mx: 'auto',
              fontSize: { xs: '0.95rem', md: '1.1rem' }
            }}
          >
            Trusted by leading companies across India for reliable logistics and transportation solutions
          </Typography>
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center" alignItems="center">
            {[
              { name: 'Client 1', logo: '/client-logo-1.png' },
              { name: 'Client 2', logo: '/client-logo-2.svg' },
              { name: 'Client 3', logo: '/client-logo-3.png' },
              { name: 'Ultratech', logo: '/ultratechclient-logo-4.jpeg' },
              { name: 'Client 5', logo: '/client-logo-5.svg.png' }
            ].map((client, index) => (
              <Grid item xs={6} sm={4} md={2} lg={2} key={index}>
                <Fade in={isVisible} timeout={1000} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      p: { xs: 2, md: 3 },
                      height: { xs: 120, md: 150 },
                      bgcolor: 'white',
                      borderRadius: '16px',
                      border: '2px solid transparent',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(25,118,210,0.1), transparent)',
                        transition: 'left 0.5s ease'
                      },
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.05)',
                        boxShadow: '0 12px 40px rgba(25,118,210,0.2)',
                        border: '2px solid #1976d2',
                        '&::before': {
                          left: '100%'
                        },
                        '& img': {
                          transform: 'scale(1.1)',
                          filter: 'brightness(1.1)'
                        }
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={client.logo}
                      alt={client.name}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        transition: 'all 0.4s ease',
                        filter: 'grayscale(30%) opacity(0.8)',
                        '&:hover': {
                          filter: 'grayscale(0%) opacity(1)'
                        }
                      }}
                    />
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box 
        id="about-section"
        sx={{ 
          bgcolor: '#f8f9fa',
          py: { xs: 6, md: 10 },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(25,118,210,0.3), transparent)'
          }
        }}
      >
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom
                sx={{ 
                  fontWeight: 800,
                  mb: 3,
                  color: '#1a237e'
                }}
              >
                About Us
              </Typography>
              <Typography 
                variant="body1" 
                paragraph
                sx={{ 
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  color: '#555',
                  mb: 3
                }}
              >
                ADVAIT ROAD MOVERS was incorporated with the aim to meet the requirement of the Cement industries firm with the view to perform the role of service to the logistic department and complete customer service for transportation solution.
              </Typography>
              <Typography 
                variant="body1" 
                paragraph
                sx={{ 
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  color: '#555'
                }}
              >
                We lift around <strong>6000 Tons monthly</strong> cement Loose and pack from Wonder Cement. Currently, we are serving the transportation of cement to Wonder Cement Private Limited, Also we are serving to <strong>Ambuja Cement, Ultratech cement</strong> as sub Transporter.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(25,118,210,0.1), rgba(41,98,255,0.1))',
                    pointerEvents: 'none'
                  }
                }}
              >
                <Box
                  sx={{
                    height: { xs: 300, md: 400 },
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <LocalShippingIcon sx={{ fontSize: 120, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ bgcolor: 'white', py: { xs: 6, md: 10 } }}>
        <Container>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            align="center" 
            sx={{ 
              mb: 2,
              fontWeight: 800,
              color: '#1a237e'
            }}
          >
            Our Core Values
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            sx={{ mb: 6, color: '#666', maxWidth: 600, mx: 'auto' }}
          >
            The principles that guide our operations and define our commitment to excellence
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {values.map((value, idx) => (
              <Grid item xs={6} sm={4} md={3} key={value.label}>
                <Fade in={isVisible} timeout={1000} style={{ transitionDelay: `${idx * 100}ms` }}>
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      height: '100%',
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
                      border: '2px solid transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 40px rgba(25,118,210,0.2)',
                        border: '2px solid #1976d2',
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
                        '& .value-icon': {
                          transform: 'scale(1.15)',
                          color: '#1976d2'
                        }
                      }
                    }}
                  >
                    <Box
                      className="value-icon"
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        color: '#1976d2',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {value.icon}
                    </Box>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        color: '#1a237e', 
                        fontWeight: 600,
                        fontSize: '0.95rem'
                      }}
                    >
                      {value.label}
                    </Typography>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section (Footer) */}
      <Box 
        sx={{ 
          bgcolor: '#1a237e',
          color: 'white',
          py: { xs: 6, md: 8 },
          position: 'relative',
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)'
          }
        }}
      >
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Get In Touch
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <LocationOnIcon sx={{ mr: 2, mt: 0.5, color: '#90caf9' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    B74 Park Plush Nr. Kanha Galaxy Khatamba Vaghodiya Road<br />
                    Vadodara 390019
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PhoneIcon sx={{ mr: 1, color: '#90caf9' }} />
                    <Typography 
                      variant="body1" 
                      component="a"
                      href="tel:9277101001"
                      sx={{ 
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Mo-9277101001
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon sx={{ mr: 1, color: '#90caf9' }} />
                    <Typography 
                      variant="body1"
                      component="a"
                      href="mailto:info@advaitroadmovers.com"
                      sx={{ 
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      info@advaitroadmovers.com
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Connect With Us
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                <Button
                  component="a"
                  href="https://www.linkedin.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    minWidth: 50,
                    height: 50,
                    borderRadius: '50%',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <LinkedInIcon />
                </Button>
                <Button
                  component="a"
                  href="https://www.facebook.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    minWidth: 50,
                    height: 50,
                    borderRadius: '50%',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <FacebookIcon />
                </Button>
                <Button
                  component="a"
                  href="https://wa.me/919277101001"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    minWidth: 50,
                    height: 50,
                    borderRadius: '50%',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <WhatsAppIcon />
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box 
            sx={{ 
              textAlign: 'center', 
              mt: 4,
              pt: 4,
              borderTop: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              © {new Date().getFullYear()} Advait Road Movers. All rights reserved. | Developed By{' '}
              <a 
                href="https://techinclusive.com/" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{ 
                  color: '#90caf9', 
                  textDecoration: 'none',
                  fontWeight: 600
                }}
              >
                Tech Inclusive
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage; 